import moment from 'moment'
import React, { FC, useEffect, useMemo, useState } from 'react'

import { Chart, Settings, LIGHT_THEME, PartialTheme, BarSeries, Axis, Position, ScaleType } from '@elastic/charts'
import { EuiPanel, EuiTitle, EuiText, EuiFlexGroup, EuiFlexItem, EuiBadge, EuiSpacer, useGeneratedHtmlId } from '@elastic/eui'

import { MasterCampaignPlan, MasterCampaignPlanCustomSection } from 'api/entities/MasterCampaignPlan'
import { MasterCampaignStub, MasterCampaignPlanCampaignBuilder, MasterCampaignPlanCampaignBuilderType } from 'api/entities/MasterCampaignStub'
import { getEndDate, getLatestEndDate } from 'utils/CampaignMathUtils'
import { formatCurrency, formatNumber, formatPercentage } from 'utils/Formatters'

export interface DeliveryMetricsProps {
  masterCampaignStub: MasterCampaignStub
  masterCampaignPlan: MasterCampaignPlan
  section?: MasterCampaignPlanCustomSection
}

interface MetricData {
  channel: MasterCampaignPlanCampaignBuilderType
  channelName: string
  icon: string
  isCpc?: boolean
}

interface ExtendedMetricData extends MetricData {
  campaignId?: string
  offAdCritterName?: string
  color?: string
  isCpc?: boolean
}

interface MonthlyData {
  month: string
  cpm: number | null
  cpc: number | null
  min: number | null
  projected: number | null
  graphProjected: number | null
  max: number | null
  graphMax: number | null
  marginOfError: number | null
}

type RateAccessor = (builder: MasterCampaignPlanCampaignBuilder) => number | undefined
type DailyVolumeCalculator = (dailyBudget: number, rate: number) => number

const cpmRate: RateAccessor = builder => builder.cpm
const cpcRate: RateAccessor = builder => builder.cpc
const impressionsFromCpm: DailyVolumeCalculator = (dailyBudget, cpm) => (dailyBudget / cpm) * 1000
const clicksFromCpc: DailyVolumeCalculator = (dailyBudget, cpc) => dailyBudget / cpc

const calculateAverageRate = (builders: MasterCampaignPlanCampaignBuilder[], getRate: RateAccessor) => {
  const buildersWithRate = builders.filter(builder => {
    const rate = getRate(builder)
    return rate && rate > 0
  })
  if (buildersWithRate.length === 0) {
    return null
  }
  return buildersWithRate.reduce((sum, builder) => sum + getRate(builder)!, 0) / buildersWithRate.length
}

const calculateMonthlyBudget = (campaign: MasterCampaignPlanCampaignBuilder, daysInMonth: number): number => {
  if (!campaign.intervalUnit || !campaign.intervalFrequency) {
    return campaign.intervalBudget
  }

  switch (campaign.intervalUnit) {
    case 'Day':
      return (campaign.intervalBudget / campaign.intervalFrequency) * daysInMonth
    case 'Week':
      return (campaign.intervalBudget / campaign.intervalFrequency / 7) * daysInMonth
    case 'Month':
      return campaign.intervalBudget / campaign.intervalFrequency
    default:
      return 0
  }
}

const calculateMonthlyVolume = (campaigns: MasterCampaignPlanCampaignBuilder[], monthMoment: moment.Moment, getRate: RateAccessor, calcDaily: DailyVolumeCalculator) => {
  return campaigns.reduce((total, campaign) => {
    const rate = getRate(campaign)
    if (rate && rate > 0 && campaign.intervalBudget) {
      const campaignStart = moment(campaign.startDate)
      const campaignEnd = getEndDate(campaign)

      if (!campaignEnd) {
        return total
      }

      const daysInMonth = monthMoment.daysInMonth()
      const monthStart = monthMoment.clone().startOf('month')
      const monthEnd = monthMoment.clone().endOf('month')

      const effectiveStart = moment.max(campaignStart.clone().startOf('day'), monthStart)
      const effectiveEnd = moment.min(campaignEnd.clone().endOf('day'), monthEnd)

      if (effectiveEnd.isBefore(effectiveStart)) {
        return total
      }

      const activeDaysInMonth = effectiveEnd.diff(effectiveStart, 'days') + 1
      const monthlyBudget = calculateMonthlyBudget(campaign, daysInMonth)
      const dailyBudget = monthlyBudget / daysInMonth
      const dailyVolume = calcDaily(dailyBudget, rate)

      return total + dailyVolume * activeDaysInMonth
    }
    return total
  }, 0)
}

const emptyMonthlyData = (monthFull: string): MonthlyData => ({
  month: monthFull, cpm: null, cpc: null, min: null, projected: null, graphProjected: null, max: null, graphMax: null, marginOfError: null
})

const generateMonthlyRateData = (
  campaignBuilders: MasterCampaignPlanCampaignBuilder[],
  marginOfError: number,
  getRate: RateAccessor,
  calcDaily: DailyVolumeCalculator,
  rateField: 'cpm' | 'cpc'
): MonthlyData[] => {
  const buildersWithRate = campaignBuilders.filter(builder => {
    const rate = getRate(builder)
    return rate && rate > 0
  })
  if (buildersWithRate.length === 0) {
    return []
  }

  const earliestStart = campaignBuilders.reduce((earliest, builder) => {
    const startDate = moment(builder.startDate)
    return !earliest || startDate.isBefore(earliest) ? startDate : earliest
  }, null as moment.Moment | null)

  const latestEnd = getLatestEndDate(campaignBuilders)

  if (!earliestStart || !latestEnd) {
    return []
  }

  const totalMonths = Math.max(12, latestEnd.diff(earliestStart, 'months') + 1)
  const months: MonthlyData[] = []

  for (let i = 0; i < totalMonths; i++) {
    const monthMoment = moment(earliestStart).add(i, 'months')
    const monthFull = monthMoment.format('MMMM YYYY')

    const activeCampaigns = campaignBuilders.filter(builder => {
      const builderStart = moment(builder.startDate)
      const builderEnd = getEndDate(builder)
      return monthMoment.isBetween(builderStart, builderEnd, 'month', '[]')
    })

    if (activeCampaigns.length === 0) {
      months.push(emptyMonthlyData(monthFull))
      continue
    }

    const activeCampaignsWithRate = activeCampaigns.filter(builder => {
      const rate = getRate(builder)
      return rate && rate > 0
    })

    if (activeCampaignsWithRate.length === 0) {
      months.push(emptyMonthlyData(monthFull))
      continue
    }

    const monthlyAverageRate = calculateAverageRate(activeCampaignsWithRate, getRate)

    if (!monthlyAverageRate) {
      months.push(emptyMonthlyData(monthFull))
      continue
    }

    const projected = calculateMonthlyVolume(activeCampaignsWithRate, monthMoment, getRate, calcDaily)

    const obj: MonthlyData = {
      ...emptyMonthlyData(monthFull),
      [rateField]: monthlyAverageRate,
      projected: Math.round(projected),
      min: Math.round(projected - projected * marginOfError),
      max: Math.round(projected + projected * marginOfError),
      marginOfError: marginOfError
    }

    obj.graphProjected = Math.round(obj.projected! - obj.min!)
    obj.graphMax = Math.round(obj.max! - obj.projected!)

    months.push(obj)
  }

  return months
}

const generateMonthlyData = (builders: MasterCampaignPlanCampaignBuilder[], marginOfError: number) =>
  generateMonthlyRateData(builders, marginOfError, cpmRate, impressionsFromCpm, 'cpm')

const generateMonthlyCpcData = (builders: MasterCampaignPlanCampaignBuilder[], marginOfError: number) =>
  generateMonthlyRateData(builders, marginOfError, cpcRate, clicksFromCpc, 'cpc')

const DeliveryMetrics: FC<DeliveryMetricsProps> = ({ masterCampaignStub, masterCampaignPlan, section }) => {
  const sectionTitle = section?.title ?? 'CPM and Impressions'
  const sectionContent = section?.content ?? 'CPM and impression projections are based on real-time publisher data and historic campaign performance. The noted margin of error is due to inherent cost variation in digital ad auctions.'
  const sectionShowTitle = section?.showTitle ?? true

  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const tilesPerRow = windowWidth < 1660 ? 2 : 3

  const impressionChartId = useGeneratedHtmlId()

  const impressionDistributionInternetData = useMemo(() => {
    if (!masterCampaignStub || !masterCampaignPlan.internetMarginOfError) {
      return []
    }
    return generateMonthlyData(
      masterCampaignStub.campaignBuilders.filter(builder => builder.campaignType === MasterCampaignPlanCampaignBuilderType.Internet),
      masterCampaignPlan.internetMarginOfError
    )
  }, [masterCampaignStub, masterCampaignStub.campaignBuilders, masterCampaignPlan.internetMarginOfError])

  const impressionDistributionTvData = useMemo(() => {
    if (!masterCampaignStub || !masterCampaignPlan.tvMarginOfError) {
      return []
    }
    return generateMonthlyData(
      masterCampaignStub.campaignBuilders.filter(builder => builder.campaignType === MasterCampaignPlanCampaignBuilderType.TV),
      masterCampaignPlan.tvMarginOfError
    )
  }, [masterCampaignStub, masterCampaignStub.campaignBuilders, masterCampaignPlan.tvMarginOfError])

  const impressionDistributionBillboardData = useMemo(() => {
    if (!masterCampaignStub || !masterCampaignPlan.billboardMarginOfError) {
      return []
    }
    return generateMonthlyData(
      masterCampaignStub.campaignBuilders.filter(builder => builder.campaignType === MasterCampaignPlanCampaignBuilderType.Billboard),
      masterCampaignPlan.billboardMarginOfError
    )
  }, [masterCampaignStub, masterCampaignStub.campaignBuilders, masterCampaignPlan.billboardMarginOfError])

  const impressionDistributionOffAdCritterData = useMemo(() => {
    if (!masterCampaignStub) {
      return new Map<string, MonthlyData[]>()
    }

    const offAdCritterCampaigns = masterCampaignStub.campaignBuilders.filter(builder => builder.campaignType === MasterCampaignPlanCampaignBuilderType.OffAdCritter && builder.includeInProjections && builder.cpm && builder.cpm > 0 && builder.minCpm && builder.maxCpm && builder.marginOfError)

    const groupedByPlatform = new Map<string, MasterCampaignPlanCampaignBuilder[]>()
    offAdCritterCampaigns.forEach(campaign => {
      const key = campaign.offAdCritterName || campaign.id
      const group = groupedByPlatform.get(key) || []
      group.push(campaign)
      groupedByPlatform.set(key, group)
    })

    const dataMap = new Map<string, MonthlyData[]>()
    groupedByPlatform.forEach((campaigns, platformName) => {
      const avgMarginOfError = campaigns.reduce((sum, c) => sum + c.marginOfError!, 0) / campaigns.length
      const monthlyData = generateMonthlyData(campaigns, avgMarginOfError)
      if (monthlyData.length > 0) {
        dataMap.set(platformName, monthlyData)
      }
    })

    return dataMap
  }, [masterCampaignStub, masterCampaignStub.campaignBuilders])

  const clickDistributionCpcData = useMemo(() => {
    if (!masterCampaignStub) {
      return new Map<string, MonthlyData[]>()
    }

    const cpcCampaigns = masterCampaignStub.campaignBuilders.filter(builder => builder.campaignType === MasterCampaignPlanCampaignBuilderType.OffAdCritter && builder.includeInProjections && builder.cpc && builder.cpc > 0 && builder.minCpc && builder.maxCpc && builder.marginOfError)

    const groupedByPlatform = new Map<string, MasterCampaignPlanCampaignBuilder[]>()
    cpcCampaigns.forEach(campaign => {
      const key = campaign.offAdCritterName || campaign.id
      const group = groupedByPlatform.get(key) || []
      group.push(campaign)
      groupedByPlatform.set(key, group)
    })

    const dataMap = new Map<string, MonthlyData[]>()
    groupedByPlatform.forEach((campaigns, platformName) => {
      const avgMarginOfError = campaigns.reduce((sum, c) => sum + c.marginOfError!, 0) / campaigns.length
      const monthlyData = generateMonthlyCpcData(campaigns, avgMarginOfError)
      if (monthlyData.length > 0) {
        dataMap.set(platformName, monthlyData)
      }
    })

    return dataMap
  }, [masterCampaignStub, masterCampaignStub.campaignBuilders])

  const chartTheme: PartialTheme = {
    axes: {
      tickLabel: {
        fontSize: 12,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
      }
    },
    colors: {
      vizColors: ['transparent', '#d47a3a', '#d47a3a']
    }
  }

  const metricsData: ExtendedMetricData[] = useMemo(() => {
    const standardPlatforms = ['Google', 'Facebook', 'Instagram', 'LinkedIn', 'TikTok']
    const metrics: ExtendedMetricData[] = []

    if (impressionDistributionTvData.length > 0) {
      metrics.push({
        channel: MasterCampaignPlanCampaignBuilderType.TV,
        channelName: 'Connected TV',
        icon: 'videoPlayer'
      })
    }

    if (impressionDistributionInternetData.length > 0) {
      metrics.push({
        channel: MasterCampaignPlanCampaignBuilderType.Internet,
        channelName: 'Internet',
        icon: 'globe'
      })
    }

    const placedPlatforms = new Set<string>()

    standardPlatforms.forEach(platformName => {
      if (impressionDistributionOffAdCritterData.has(platformName)) {
        const firstCampaign = masterCampaignStub?.campaignBuilders.find(c => c.offAdCritterName === platformName)
        metrics.push({
          channel: MasterCampaignPlanCampaignBuilderType.Internet,
          channelName: platformName,
          icon: 'globe',
          offAdCritterName: platformName,
          color: firstCampaign?.offAdCritterColor
        })
        placedPlatforms.add(platformName)
      } else if (clickDistributionCpcData.has(platformName)) {
        const firstCampaign = masterCampaignStub?.campaignBuilders.find(c => c.offAdCritterName === platformName)
        metrics.push({
          channel: MasterCampaignPlanCampaignBuilderType.Internet,
          channelName: platformName,
          icon: 'globe',
          offAdCritterName: platformName,
          color: firstCampaign?.offAdCritterColor,
          isCpc: true
        })
        placedPlatforms.add(platformName)
      }
    })

    if (impressionDistributionBillboardData.length > 0) {
      metrics.push({
        channel: MasterCampaignPlanCampaignBuilderType.Billboard,
        channelName: 'Digital Billboard',
        icon: 'mapMarker'
      })
    }

    impressionDistributionOffAdCritterData.forEach((_, platformName) => {
      if (!placedPlatforms.has(platformName)) {
        const firstCampaign = masterCampaignStub?.campaignBuilders.find(c => c.offAdCritterName === platformName)
        metrics.push({
          channel: MasterCampaignPlanCampaignBuilderType.Internet,
          channelName: platformName,
          icon: 'globe',
          offAdCritterName: platformName,
          color: firstCampaign?.offAdCritterColor
        })
        placedPlatforms.add(platformName)
      }
    })

    clickDistributionCpcData.forEach((_, platformName) => {
      if (!placedPlatforms.has(platformName)) {
        const firstCampaign = masterCampaignStub?.campaignBuilders.find(c => c.offAdCritterName === platformName)
        metrics.push({
          channel: MasterCampaignPlanCampaignBuilderType.Internet,
          channelName: platformName,
          icon: 'globe',
          offAdCritterName: platformName,
          color: firstCampaign?.offAdCritterColor,
          isCpc: true
        })
      }
    })

    return metrics
  }, [impressionDistributionTvData.length, impressionDistributionInternetData.length, impressionDistributionBillboardData.length, impressionDistributionOffAdCritterData, clickDistributionCpcData, masterCampaignStub?.campaignBuilders])

  const getRowForItem = (totalItems: number, itemIndex: number): number => {
    if (totalItems <= 3) {
      return 0
    }

    if (totalItems === 4) {
      return itemIndex < 2 ? 0 : 1
    }

    const fullRows = Math.floor(totalItems / 3)
    const remainder = totalItems % 3

    if (remainder === 0) {
      return Math.floor(itemIndex / 3)
    }

    if (remainder === 1) {
      const itemsInFirstNMinusOneRows = (fullRows - 1) * 3
      if (itemIndex < itemsInFirstNMinusOneRows) {
        return Math.floor(itemIndex / 3)
      }
      return fullRows - 1 + Math.floor((itemIndex - itemsInFirstNMinusOneRows) / 2)
    }

    if (remainder === 2) {
      const itemsInFullRows = fullRows * 3
      if (itemIndex < itemsInFullRows) {
        return Math.floor(itemIndex / 3)
      }
      return fullRows
    }

    return Math.floor(itemIndex / 3)
  }

  const getItemsInRow = (totalItems: number, rowIndex: number): number => {
    if (totalItems <= 3) {
      return totalItems
    }

    if (totalItems === 4) {
      return 2
    }

    const fullRows = Math.floor(totalItems / 3)
    const remainder = totalItems % 3

    if (remainder === 0) {
      return 3
    }

    if (remainder === 1) {
      if (rowIndex >= fullRows - 1) {
        return 2
      }
      return 3
    }

    if (remainder === 2) {
      if (rowIndex === fullRows) {
        return 2
      }
      return 3
    }

    return 3
  }

  const organizeMetricsIntoRows = (metrics: ExtendedMetricData[], perRow: number): ExtendedMetricData[][] => {
    const totalItems = metrics.length

    if (totalItems === 0) {
      return []
    }

    if (totalItems <= perRow) {
      return [metrics]
    }

    if (totalItems === perRow + 1) {
      const half = Math.ceil(totalItems / 2)
      return [metrics.slice(0, half), metrics.slice(half)]
    }

    const fullRows = Math.floor(totalItems / perRow)
    const remainder = totalItems % perRow
    const rows: ExtendedMetricData[][] = []

    if (remainder === 0) {
      for (let i = 0; i < fullRows; i++) {
        rows.push(metrics.slice(i * perRow, (i + 1) * perRow))
      }
      return rows
    }

    if (remainder === 1) {
      for (let i = 0; i < fullRows - 1; i++) {
        rows.push(metrics.slice(i * perRow, (i + 1) * perRow))
      }
      const remaining = metrics.slice((fullRows - 1) * perRow)
      const half = Math.ceil(remaining.length / 2)
      rows.push(remaining.slice(0, half))
      rows.push(remaining.slice(half))
      return rows
    }

    for (let i = 0; i < fullRows; i++) {
      rows.push(metrics.slice(i * perRow, (i + 1) * perRow))
    }
    rows.push(metrics.slice(fullRows * perRow))
    return rows
  }

  const metricsRows = useMemo(() => organizeMetricsIntoRows(metricsData, tilesPerRow), [metricsData, tilesPerRow])

  const yAccessorFn = (d: MonthlyData) => d.graphProjected
  yAccessorFn.fieldName = 'Projected'

  if (section?.hideAll === true) {
    return null
  }

  return (
    <EuiPanel paddingSize='l'>
      <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
        <EuiFlexItem grow={false}>
          {sectionShowTitle && (
            <EuiTitle size='m'>
              <h2>{sectionTitle}</h2>
            </EuiTitle>
          )}
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size='s' />
      <EuiText size='m' color='subdued' style={{ whiteSpace: 'pre-wrap' }}>
        {sectionContent}
      </EuiText>

      <EuiSpacer size='l' />

      {metricsRows.map((row, rowIndex) => (
        <>
          <EuiFlexGroup gutterSize='l' wrap={false} key={`row-${rowIndex}`}>
            {row.map((metric, metricIndex) => {
              const index = metricsRows.slice(0, rowIndex).reduce((sum, r) => sum + r.length, 0) + metricIndex
              let distributionData: MonthlyData[] = []

              if (metric.isCpc && metric.offAdCritterName) {
                distributionData = clickDistributionCpcData.get(metric.offAdCritterName) || []
              } else if (metric.offAdCritterName) {
                distributionData = impressionDistributionOffAdCritterData.get(metric.offAdCritterName) || []
              } else {
                switch (metric.channel) {
                  case MasterCampaignPlanCampaignBuilderType.Internet:
                    distributionData = impressionDistributionInternetData
                    break
                  case MasterCampaignPlanCampaignBuilderType.TV:
                    distributionData = impressionDistributionTvData
                    break
                  case MasterCampaignPlanCampaignBuilderType.Billboard:
                    distributionData = impressionDistributionBillboardData
                    break
                  default:
                    distributionData = []
                }
              }

              let targetRate = 0
              let marginOfError = 0

              if (distributionData.length > 0) {
                if (metric.isCpc) {
                  const validDataPoints = distributionData.filter(d => d.cpc && d.marginOfError)
                  if (validDataPoints.length > 0) {
                    targetRate = validDataPoints.reduce((sum, d) => sum + (d.cpc || 0), 0) / validDataPoints.length
                    marginOfError = validDataPoints[0].marginOfError || 0
                  }
                } else {
                  const validDataPoints = distributionData.filter(d => d.cpm && d.marginOfError)
                  if (validDataPoints.length > 0) {
                    targetRate = validDataPoints.reduce((sum, d) => sum + (d.cpm || 0), 0) / validDataPoints.length
                    marginOfError = validDataPoints[0].marginOfError || 0
                  }
                }
              }

              const totalMinImpression = distributionData.reduce((min, d) => min + (d.min || 0), 0)
              const totalProjectedImpression = distributionData.reduce((projected, d) => projected + (d.projected || 0), 0)
              const totalMaxImpression = distributionData.reduce((max, d) => max + (d.max || 0), 0)

              const maxImpression = distributionData.reduce((max, d) => Math.max(max, d.max || 0), 0)

              const rateLabel = metric.isCpc ? 'Projected Avg. CPC' : 'Projected Avg. CPM'
              const totalLabel = metric.isCpc ? 'Total Projected Clicks' : metric.channelName === 'Internet' ? 'Total Projected Impressions' : 'Total Projected Views'
              const chartLabel = metric.isCpc ? 'Clicks by Month' : metric.channelName === 'Internet' ? 'Impression by Month' : 'Views by Month'

              return (
                <EuiFlexItem key={index} grow={3}>
                  <EuiPanel
                    paddingSize='l'
                    hasShadow={true}
                    style={{
                      height: '100%',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                    }}>
                    <EuiFlexGroup justifyContent='spaceBetween' alignItems='center'>
                      <EuiFlexItem grow={false}>
                        <EuiFlexGroup alignItems='center' gutterSize='s'>
                          <EuiFlexItem grow={false}>
                            <EuiTitle size='xs'>
                              <h3>{metric.channelName}</h3>
                            </EuiTitle>
                          </EuiFlexItem>
                        </EuiFlexGroup>
                      </EuiFlexItem>
                    </EuiFlexGroup>

                    <EuiSpacer size='m' />

                    <EuiFlexGroup alignItems='baseline'>
                      <EuiFlexItem grow={false}>
                        <EuiText size='s'>{rateLabel}</EuiText>
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiBadge color='#F0B051'>
                          <EuiText size='m' style={{ fontWeight: 'bold' }}>
                            {formatCurrency(targetRate)}
                          </EuiText>
                        </EuiBadge>
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiText size='xs' color='subdued'>
                          ±{formatPercentage(marginOfError)}
                        </EuiText>
                      </EuiFlexItem>
                    </EuiFlexGroup>

                    <EuiSpacer size='m' />

                    <EuiText size='s' style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      {totalLabel}
                    </EuiText>
                    <EuiFlexGroup gutterSize='s'>
                      <EuiFlexItem>
                        <EuiPanel paddingSize='s' style={{ textAlign: 'center', backgroundColor: 'var(--euiColorLightShade)' }}>
                          <EuiText size='xs' color='subdued'>
                            Minimum
                          </EuiText>
                          <EuiText size='m' color='subdued'>
                            {formatNumber(totalMinImpression, 0, 0)}
                          </EuiText>
                        </EuiPanel>
                      </EuiFlexItem>
                      <EuiFlexItem>
                        <EuiPanel paddingSize='s' style={{ textAlign: 'center', backgroundColor: 'var(--euiColorLightShade)', borderStyle: 'solid', borderColor: '#F0B051' }}>
                          <EuiText size='xs' style={{ fontWeight: 'bold' }}>
                            Projected
                          </EuiText>
                          <EuiText size='m' style={{ fontWeight: 'bold' }}>
                            {formatNumber(totalProjectedImpression, 0, 0)}
                          </EuiText>
                        </EuiPanel>
                      </EuiFlexItem>
                      <EuiFlexItem>
                        <EuiPanel paddingSize='s' style={{ textAlign: 'center', backgroundColor: 'var(--euiColorLightShade)' }}>
                          <EuiText size='xs' color='subdued'>
                            Maximum
                          </EuiText>
                          <EuiText size='m' color='subdued'>
                            {formatNumber(totalMaxImpression, 0, 0)}
                          </EuiText>
                        </EuiPanel>
                      </EuiFlexItem>
                    </EuiFlexGroup>

                    <EuiSpacer size='m' />

                    <div>
                      <EuiText size='s' style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                        {chartLabel}
                      </EuiText>
                      <EuiPanel paddingSize='s' style={{ backgroundColor: 'var(--euiColorEmptyShade)' }}>
                        <div style={{ height: '300px', position: 'relative' }}>
                          <Chart id={impressionChartId + index} size={{ height: 300 }}>
                            <Settings
                              theme={chartTheme}
                              baseTheme={LIGHT_THEME}
                              showLegend={false}
                              tooltip={{
                                customTooltip: ({ values }) => {
                                  const dataPoint = values.find(v => (v.seriesIdentifier as any).yAccessor === 'Projected')
                                  if (!dataPoint) {
                                    return null
                                  }

                                  const monthlyData = dataPoint.datum as MonthlyData

                                  if (!monthlyData.projected || !monthlyData.min || !monthlyData.max) {
                                    return null
                                  }

                                  return (
                                    <EuiPanel paddingSize='s' hasShadow={true} style={{ minWidth: '140px' }}>
                                      <EuiTitle size='s'>
                                        <h3 style={{ marginBottom: '4px' }}>{monthlyData.month}</h3>
                                      </EuiTitle>
                                      <EuiText size='s' style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                        Projected: {formatNumber(monthlyData.projected, 0, 0)}
                                      </EuiText>
                                      <EuiText size='xs' color='subdued'>
                                        Min: {formatNumber(monthlyData.min, 0, 0)}
                                      </EuiText>
                                      <EuiText size='xs' color='subdued'>
                                        Max: {formatNumber(monthlyData.max, 0, 0)}
                                      </EuiText>
                                    </EuiPanel>
                                  )
                                }
                              }}
                            />
                            <BarSeries
                              id='Projected'
                              data={distributionData}
                              xAccessor='month'
                              yAccessors={['min', yAccessorFn, 'graphMax']}
                              stackAccessors={['min', yAccessorFn, 'graphMax']}
                              xScaleType={ScaleType.Ordinal}
                              yScaleType={ScaleType.Linear}
                              tickFormat={value => formatNumber(value, 0, 0)}
                              filterSeriesInTooltip={(d: any) => d.seriesKeys.includes('Projected')}
                              barSeriesStyle={{
                                rectBorder: {
                                  visible: true,
                                  stroke: 'transparent',
                                  strokeWidth: 0.5
                                }
                              }}
                            />
                            <Axis id='impressions' position={Position.Left} tickFormat={value => formatNumber(value, 0, 0)} domain={{ min: 0, max: maxImpression + maxImpression * 0.2 }} />
                            <Axis
                              id='months'
                              position={Position.Bottom}
                              style={{
                                tickLabel: {
                                  rotation: 0
                                }
                              }}
                              labelFormat={d => d.charAt(0)}
                            />
                          </Chart>
                        </div>
                      </EuiPanel>
                    </div>
                  </EuiPanel>
                </EuiFlexItem>
              )
            })}
          </EuiFlexGroup>
          {rowIndex < metricsRows.length - 1 && <EuiSpacer size='l' />}
        </>
      ))}

      <EuiSpacer size='l' />
    </EuiPanel>
  )
}

export default DeliveryMetrics
